import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Country } from './countries';
import { flagOf, searchCountries } from './countries';
import { countryName, t, useLanguage } from '../i18n';

const nameOf = (country: Country) => countryName(country.iso, country.name);

interface PhoneFieldProps {
  country: Country;
  onCountryChange: (country: Country) => void;
  number: string;
  onNumberChange: (number: string) => void;
}

/** WhatsApp number: a searchable country menu with flags, then the local number. */
export function PhoneField({ country, onCountryChange, number, onNumberChange }: PhoneFieldProps) {
  useLanguage();
  const [open, setOpen] = useState(false);
  const numberRef = useRef<HTMLInputElement>(null);
  return (
    <div className="phone-field">
      <div className="phone-field__row">
        <button type="button" className="phone-field__country" aria-haspopup="listbox" aria-expanded={open} aria-label={t('phone.country', { name: nameOf(country), dial: country.dial })} onClick={() => setOpen(!open)}>
          <span className="phone-field__flag">{flagOf(country.iso)}</span>
          <span>+{country.dial}</span>
          <svg className="phone-field__caret" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 4.5L6 8l3.5-3.5" />
          </svg>
        </button>
        <input
          ref={numberRef}
          className="phone-field__number"
          type="tel"
          value={number}
          onChange={(event) => onNumberChange(event.target.value)}
          placeholder={t('phone.placeholder')}
          inputMode="tel"
          autoComplete="tel-national"
          aria-label={t('auth.whatsapp')}
          required
        />
      </div>
      {open ? (
        <CountryMenu
          selected={country}
          onPick={(picked) => {
            onCountryChange(picked);
            setOpen(false);
            numberRef.current?.focus();
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function CountryMenu({ selected, onPick, onClose }: { selected: Country; onPick: (country: Country) => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const matches = searchCountries(query, nameOf);

  // Close on a tap or click outside the menu.
  useEffect(() => {
    const away = (event: Event) => {
      if (rootRef.current && !rootRef.current.parentElement?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
    };
  }, [onClose]);

  // Keep the highlighted country in view while moving with the arrow keys.
  useEffect(() => {
    const item = listRef.current?.children[highlight] as HTMLElement | undefined;
    if (item && typeof item.scrollIntoView === 'function') item.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') setHighlight(Math.min(highlight + 1, matches.length - 1));
    else if (event.key === 'ArrowUp') setHighlight(Math.max(highlight - 1, 0));
    else if (event.key === 'Enter') {
      const country = matches[highlight];
      if (country) onPick(country);
    } else if (event.key === 'Escape') onClose();
    else return;
    event.preventDefault();
  };

  return (
    <div ref={rootRef} className="country-menu">
      <input
        className="country-menu__search"
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlight(0);
        }}
        onKeyDown={onKeyDown}
        placeholder={t('phone.search')}
        aria-label={t('phone.search')}
        autoComplete="off"
        autoFocus
      />
      <ul ref={listRef} className="country-menu__list" role="listbox" aria-label={t('phone.countries')}>
        {matches.map((country, index) => (
          <li key={country.iso} role="option" aria-selected={country.iso === selected.iso}>
            <button type="button" className={`country-menu__item${index === highlight ? ' country-menu__item--active' : ''}`} onClick={() => onPick(country)} onMouseEnter={() => setHighlight(index)}>
              <span className="phone-field__flag">{flagOf(country.iso)}</span>
              <span className="country-menu__name">{nameOf(country)}</span>
              <span className="muted">+{country.dial}</span>
            </button>
          </li>
        ))}
        {matches.length === 0 ? <li className="muted country-menu__empty">{t('phone.noCountry')}</li> : null}
      </ul>
    </div>
  );
}
